"use client";

import { useEffect, useState } from "react";
import type { OrderOutput } from "@/interfaces";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
  type PieLabelRenderProps,
} from "recharts";
import { differenceInDays } from "date-fns";
import { numberToMoney } from "@/util/moneyConversion";

interface CategoryData extends Record<string, unknown> {
  name: string;
  value: number;
  count: number;
  color: string;
}

interface DelinquencyByTimeChartProps {
  orders: OrderOutput[];
}

export function DelinquencyByTimeChart({
  orders,
}: DelinquencyByTimeChartProps) {
  const [data, setData] = useState<CategoryData[]>([]);

  useEffect(() => {
    if (!orders?.length) {
      setData([]);
      return;
    }

    const categories: Record<
      string,
      { count: number; amount: number; color: string }
    > = {
      "Até 30 dias": { count: 0, amount: 0, color: "#60a5fa" }, // blue
      "31-60 dias": { count: 0, amount: 0, color: "#fbbf24" }, // yellow
      "61-90 dias": { count: 0, amount: 0, color: "#f97316" }, // orange
      "Mais de 90 dias": { count: 0, amount: 0, color: "#ef4444" }, // red
    };

    const today = new Date();

    orders.forEach((order) => {
      if (!order.preparationDate) return;

      const orderDate = new Date(order.preparationDate);
      const daysDiff = differenceInDays(today, orderDate);

      const paidAmount =
        order.payments?.reduce((sum, payment) => sum + payment.amount, 0) || 0;
      const pendingAmount = order.totalAmount - paidAmount;

      let category: string;

      if (daysDiff <= 30) {
        category = "Até 30 dias";
      } else if (daysDiff <= 60) {
        category = "31-60 dias";
      } else if (daysDiff <= 90) {
        category = "61-90 dias";
      } else {
        category = "Mais de 90 dias";
      }

      categories[category].count += 1;
      categories[category].amount += pendingAmount;
    });

    const chartData = Object.entries(categories).map(
      ([name, { count, amount, color }]) => ({
        name,
        value: amount,
        count,
        color,
      })
    );

    setData(chartData);
  }, [orders]);

  if (!data.length) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground text-center">
          Não há dados de inadimplência por tempo para exibir.
        </p>
      </div>
    );
  }

  const renderLabel = ({ name, percent }: PieLabelRenderProps) => {
    const percentage = Math.round(Number(percent ?? 0) * 100);
    return `${name ?? ""}: ${percentage}%`;
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          labelLine={false}
          outerRadius={80}
          fill="#8884d8"
          dataKey="value"
          label={renderLabel}
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} />
          ))}
        </Pie>
        <Tooltip
          formatter={(value: number) => [
            numberToMoney(value),
            "Valor Pendente",
          ]}
        />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );
}
